import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Typography, 
  Avatar, 
  Row, 
  Col, 
  Button, 
  Space, 
  Tag,
  Form, 
  Input, 
  message,
  Divider,
  Statistic
} from 'antd';
import { 
  UserOutlined, 
  MailOutlined, 
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  GithubOutlined,
  LinkedinOutlined,
  LinkOutlined,
  BookOutlined,
  HeartOutlined,
  EyeOutlined,
  FileTextOutlined,
  ToolOutlined,
  StarOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import PostCard from '../../components/PostCard';
import { defaultAboutInfoTemplate, posts} from '../../api/mockData';
import './index.css';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const AboutPage = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm] = Form.useForm();
  const [userPosts, setUserPosts] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const userStr = localStorage.getItem('currentUser');
    if (userStr) {
      const user = JSON.parse(userStr);
      setCurrentUser(user);
      editForm.setFieldsValue(user);
      
      // 获取用户的文章
      const userPosts = posts.filter(post => post.authorId === user.id);
      setUserPosts(userPosts);
    } else {
      setCurrentUser(null);
    }
  }, [editForm]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = (values) => {
    const updatedUser = {
      ...currentUser,
      ...values,
      id: currentUser.id
    };
    
    localStorage.setItem('currentUser', JSON.stringify(updatedUser));
    setCurrentUser(updatedUser);
    setIsEditing(false);
    message.success('个人信息已更新');
  };

  const handleCancel = () => {
    editForm.setFieldsValue(currentUser);
    setIsEditing(false);
  };

  const getUserStats = () => {
    if (!currentUser) return {};
    
    const userPosts = posts.filter(post => post.authorId === currentUser.id);
    return {
      postCount: userPosts.length,
      totalViews: userPosts.reduce((sum, post) => sum + post.readCount, 0),
      totalLikes: userPosts.reduce((sum, post) => sum + post.likes, 0),
      avgReadTime: userPosts.length > 0 ? 
        Math.round(userPosts.reduce((sum, post) => sum + (post.content?.length || 0), 0) / userPosts.length / 500) : 0
    };
  };

  const stats = getUserStats();
  const userToDisplay = currentUser || defaultAboutInfoTemplate;

  if (!currentUser) {
    return (
      <div className="about-page">
        <div className="about-container">
          <Card className="no-login">
            <div className="login-prompt">
              <UserOutlined style={{ fontSize: '64px', color: '#1890ff', marginBottom: '24px' }} />
              <Title level={3}>请先登录</Title>
              <Paragraph type="secondary" style={{ marginBottom: '32px' }}>
                登录后查看个人主页，管理您的文章和设置
              </Paragraph>
              <Space>
                <Button type="primary" onClick={() => navigate('/auth')}>
                  立即登录
                </Button>
                <Button onClick={() => navigate('/')}>
                  返回首页
                </Button>
              </Space>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="about-page">
      <div className="about-container">
        {/* 个人信息部分 */}
        <div className="profile-section">
          <Card className="profile-header">
            <Avatar 
              className="profile-avatar"
              src={userToDisplay.avatar} 
              icon={<UserOutlined />}
              size={120}
            />
            <Title level={2} className="profile-name">
              {userToDisplay.name}
            </Title>
            <Paragraph className="profile-intro">
              {userToDisplay.intro}
            </Paragraph>
            
            <Button 
              type="primary" 
              icon={<EditOutlined />}
              className="edit-button"
              onClick={handleEdit}
            >
              编辑资料
            </Button>
          </Card>
          
          <Card className="profile-content">
            {isEditing ? (
              <Form
                form={editForm}
                layout="vertical"
                onFinish={handleSave}
                className="edit-form"
              >
                <Form.Item
                  label="昵称"
                  name="name"
                  rules={[{ required: true, message: '请输入昵称' }]}
                >
                  <Input />
                </Form.Item>
                
                <Form.Item
                  label="个人简介"
                  name="intro"
                >
                  <TextArea rows={2} />
                </Form.Item>
                
                <Form.Item
                  label="详细描述"
                  name="details"
                >
                  <TextArea rows={4} />
                </Form.Item>
                
                <Form.Item
                  label="邮箱"
                  name="email"
                  rules={[{ type: 'email', message: '请输入有效的邮箱地址' }]}
                >
                  <Input />
                </Form.Item>
                
                <Form.Item
                  label="技术栈"
                  name="techStack"
                >
                  <Input />
                </Form.Item>
                
                <Form.Item
                  label="兴趣爱好"
                  name="interests"
                >
                  <Input />
                </Form.Item>
                
                <div className="form-actions">
                  <Button onClick={handleCancel} icon={<CloseOutlined />}>
                    取消
                  </Button>
                  <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
                    保存
                  </Button>
                </div>
              </Form>
            ) : (
              <div className="profile-details">
                <div className="detail-item">
                  <div className="detail-label">
                    <BookOutlined className="detail-icon" />
                    详细描述
                  </div>
                  <Paragraph className="detail-value">
                    {userToDisplay.details}
                  </Paragraph>
                </div>
                
                <Row gutter={[24, 16]}>
                  <Col xs={24} sm={12}>
                    <div className="detail-item">
                      <div className="detail-label">
                        <MailOutlined className="detail-icon" />
                        邮箱
                      </div>
                      <Text className="detail-value">{userToDisplay.email}</Text>
                    </div>
                  </Col>
                  
                  <Col xs={24} sm={12}>
                    <div className="detail-item">
                      <div className="detail-label">
                        <ToolOutlined className="detail-icon" />
                        技术栈
                      </div>
                      <div className="detail-value">
                        {userToDisplay.techStack.split(',').map(tech => (
                          <Tag key={tech.trim()} color="blue" style={{ margin: '2px' }}>
                            {tech.trim()}
                          </Tag>
                        ))}
                      </div>
                    </div>
                  </Col>
                  
                  <Col xs={24} sm={12}>
                    <div className="detail-item">
                      <div className="detail-label">
                        <StarOutlined className="detail-icon" />
                        兴趣爱好
                      </div>
                      <div className="detail-value">
                        {userToDisplay.interests.split(',').map(interest => (
                          <Tag key={interest.trim()} color="green" style={{ margin: '2px' }}>
                            {interest.trim()}
                          </Tag>
                        ))}
                      </div>
                    </div>
                  </Col>
                  
                  <Col xs={24} sm={12}>
                    <div className="detail-item">
                      <div className="detail-label">
                        <UserOutlined className="detail-icon" />
                        用户名
                      </div>
                      <Text className="detail-value">{currentUser?.username || '游客'}</Text>
                    </div>
                  </Col>
                </Row>
                
                <Divider />
                
                <div className="detail-item">
                  <div className="detail-label">
                    <LinkOutlined className="detail-icon" />
                    社交链接
                  </div>
                  <div className="social-links">
                    <a href={userToDisplay.github} target="_blank" rel="noopener noreferrer" className="social-link">
                      <GithubOutlined />
                    </a>
                    <a href={userToDisplay.linkedin} target="_blank" rel="noopener noreferrer" className="social-link">
                      <LinkedinOutlined />
                    </a>
                    <a href={userToDisplay.personalHomepage} target="_blank" rel="noopener noreferrer" className="social-link">
                      <LinkOutlined />
                    </a>
                  </div>
                </div>
              </div>
            )}
          </Card>
        </div>

        {/* 统计数据 */}
        <div className="stats-section">
          <Title level={3} className="section-title">
            <FileTextOutlined /> 我的数据
          </Title>
          <div className="stats-cards">
            <Card className="stat-card">
              <FileTextOutlined className="stat-icon" style={{ color: '#1890ff' }} />
              <Statistic
                className="stat-number"
                value={stats.postCount}
                suffix="篇"
              />
              <Text className="stat-label">发表文章</Text>
            </Card>
            
            <Card className="stat-card">
              <EyeOutlined className="stat-icon" style={{ color: '#52c41a' }} />
              <Statistic
                className="stat-number"
                value={stats.totalViews}
              />
              <Text className="stat-label">总阅读量</Text>
            </Card>
            
            <Card className="stat-card">
              <HeartOutlined className="stat-icon" style={{ color: '#f5222d' }} />
              <Statistic
                className="stat-number"
                value={stats.totalLikes}
              />
              <Text className="stat-label">总点赞数</Text>
            </Card>
            
            <Card className="stat-card">
              <BookOutlined className="stat-icon" style={{ color: '#722ed1' }} />
              <Statistic
                className="stat-number"
                value={stats.avgReadTime}
                suffix="分钟"
              />
              <Text className="stat-label">平均阅读时长</Text>
            </Card>
          </div>
        </div>

        {/* 我的文章 */}
        <div className="recent-posts">
          <Title level={3} className="section-title">
            <FileTextOutlined /> 我的文章
          </Title>
          {userPosts.length > 0 ? (
            <div className="posts-grid">
              {userPosts.slice(0, 4).map(post => (
                <PostCard key={post.id} post={post} showAuthor={false} />
              ))}
            </div>
          ) : (
            <Card>
              <div style={{ textAlign: 'center', padding: '32px' }}>
                <FileTextOutlined style={{ fontSize: '48px', color: '#999', marginBottom: '16px' }} />
                <Title level={4} style={{ color: '#999' }}>暂无文章</Title>
                <Text type="secondary">开始你的第一篇创作吧！</Text>
                <div style={{ marginTop: '24px' }}>
                  <Button type="primary" onClick={() => navigate('/write')}>
                    写文章
                  </Button>
                </div>
              </div>
            </Card>
          )}
          
          {userPosts.length > 4 && (
            <div style={{ textAlign: 'center', marginTop: '24px' }}>
              <Button type="link" onClick={() => navigate('/archive')}>
                查看全部文章 →
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AboutPage;